import styled from '@emotion/styled';
import type { EventStatus } from 'models/setup/PrjSchedule';
import { getScheduleStyle } from 'utils/CalendarUtils';

export const AppContainer = styled.div`
  width: 100%;
  height: 100vh;
  background-color: #f8fafc;
  color: #1e293b;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    sans-serif;
`;

export const CalendarRoot = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  overflow-y: hidden;
  background-color: #f8fafc;
  box-sizing: border-box;
`;

export const CalTopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  flex-shrink: 0;
  gap: 12px;
`;

export const CalNavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CalNavArrowBtn = styled.button`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  border-radius: 6px;
  color: #0284c7;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f0f9ff;
    border-color: #0284c7;
  }
`;

export const CalTodayBtn = styled.button`
  padding: 4px 14px;
  height: 32px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #0284c7;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f0f9ff;
    border-color: #0284c7;
  }
`;

export const CalDateTitle = styled.span`
  font-size: 19px;
  font-weight: 700;
  color: #0f172a;
  margin-left: 10px;
  letter-spacing: -0.5px;
`;

export const CalControlsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const CalViewToggle = styled.div`
  display: inline-flex;
  border: 1px solid #0284c7;
  border-radius: 6px;
  overflow: hidden;
  background: #ffffff;
`;

export const CalViewBtn = styled.button<{ active?: boolean }>`
  padding: 4px 16px;
  height: 32px;
  border: none;
  background: ${({ active }) => (active ? '#0284c7' : '#ffffff')};
  color: ${({ active }) => (active ? '#ffffff' : '#0284c7')};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  & + & {
    border-left: 1px solid #0284c7;
  }
`;

export const CalAddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 16px;
  background: #0284c7;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(2, 132, 199, 0.2);

  &:hover {
    background: #0369a1;
  }
`;

export const CalLegendBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 20px;
  background: #ffffff;
  border-bottom: 1px solid #edf2f7;
  font-size: 12px;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 12px;
`;

export const LegendGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const LegendColorBox = styled.span<{ statusType: EventStatus }>`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  ${({ statusType }) => {
    const style = getScheduleStyle(statusType);
    return `
      background-color: ${style.bg};
      border: ${style.borderWidth} solid ${style.border};
    `;
  }}
`;

export const LegendCategoriesList = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
`;

export const LegendTagBadge = styled.span`
  padding: 2px 6px;
  background: #f1f5f9;
  border-radius: 4px;
  font-weight: 500;
  font-size: 11px;
  color: #475569;
`;

export const CalMainContainer = styled.main`
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
  box-sizing: border-box;
`;

export const CalBoard = styled.div<{ minWidth?: number; isExpanded?: boolean }>`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: ${({ isExpanded }) => (isExpanded ? 'auto' : '100%')};
  min-height: 600px;
  min-width: ${({ minWidth }) => (minWidth ? `${minWidth}px` : '1080px')};
  box-sizing: border-box;
`;

export const CalGridHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(140px, 1fr));
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
`;

export const CalHeaderCell = styled.div<{
  isSunday?: boolean;
  isSaturday?: boolean;
}>`
  padding: 8px 4px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: ${({ isSunday, isSaturday }) =>
    isSunday ? '#ef4444' : isSaturday ? '#3b82f6' : '#334155'};
  border-right: 1px solid #e2e8f0;

  &:last-child {
    border-right: none;
  }
`;

export const CalGridBody = styled.div<{ weekCount: number; isExpanded?: boolean }>`
  display: grid;
  flex: 1;
  min-height: ${({ isExpanded }) => (isExpanded ? 'auto' : '550px')};
  grid-template-rows: repeat(
    ${({ weekCount }) => weekCount},
    ${({ isExpanded }) => (isExpanded ? 'minmax(100px, auto)' : 'minmax(100px, 1fr)')}
  );
`;

export const CalGridRow = styled.div<{ isExpanded?: boolean }>`
  display: grid;
  grid-template-columns: repeat(7, minmax(140px, 1fr));
  border-bottom: 1px solid #e2e8f0;
  min-height: 100px;
  height: ${({ isExpanded }) => (isExpanded ? 'auto' : 'auto')};

  &:last-child {
    border-bottom: none;
  }
`;

export const CalDayCell = styled.div<{
  isOtherMonth?: boolean;
  isToday?: boolean;
  isSunday?: boolean;
  isSaturday?: boolean;
  isDragOver?: boolean;
  isExpanded?: boolean;
}>`
  border-right: 1px solid #e2e8f0;
  padding: 3px 5px;
  display: flex;
  flex-direction: column;
  background-color: ${({ isOtherMonth, isDragOver }) =>
    isDragOver ? '#f0f9ff' : isOtherMonth ? '#fafbfc' : '#ffffff'};
  position: relative;
  transition: background-color 0.15s ease, outline 0.15s ease;
  cursor: pointer;
  min-width: 140px;
  min-height: 100px;
  height: ${({ isExpanded }) => (isExpanded ? 'auto' : 'auto')};
  overflow: ${({ isExpanded }) => (isExpanded ? 'visible' : 'hidden')};
  box-sizing: border-box;

  ${({ isDragOver }) =>
    isDragOver &&
    `
    outline: 2px dashed #0284c7;
    outline-offset: -2px;
  `}

  &:last-child {
    border-right: none;
  }

  &:hover {
    background-color: ${({ isDragOver }) => (isDragOver ? '#f0f9ff' : '#f8fafc')};
  }
`;

export const CalCellHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 2px;
  height: 22px;
  flex-shrink: 0;
`;

export const CalDateNum = styled.span<{
  isToday?: boolean;
  isSunday?: boolean;
  isOtherMonth?: boolean;
}>`
  font-size: 13px;
  font-weight: ${({ isToday }) => (isToday ? '700' : '600')};
  color: ${({ isToday, isSunday, isOtherMonth }) =>
    isToday ? '#ffffff' : isOtherMonth ? '#94a3b8' : isSunday ? '#ef4444' : '#334155'};
  background-color: ${({ isToday }) => (isToday ? '#0284c7' : 'transparent')};
  min-width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  user-select: none;
`;

export const CalEventsStack = styled.div<{ isExpanded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-height: 50px;
  overflow: ${({ isExpanded }) => (isExpanded ? 'visible' : 'hidden')};
`;

export const IncompleteAlertBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 13px;
  height: 13px;
  min-width: 13px;
  border-radius: 50%;
  background-color: #ef4444;
  color: #ffffff;
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
  margin-right: 4px;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(239, 68, 68, 0.3);
`;

export const DelayedTagBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  height: 14px;
  border-radius: 3px;
  background-color: #ef4444;
  color: #ffffff;
  font-size: 8.5px;
  font-weight: 700;
  line-height: 1;
  margin-left: 4px;
  flex-shrink: 0;
  letter-spacing: -0.2px;
`;

export const CalEventChip = styled.div<{
  statusType: EventStatus;
  wktTpCd?: string;
  isDimmed?: boolean;
}>`
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 11px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: ${({ statusType }) => (statusType === 'complete' ? 'pointer' : 'grab')};
  transition: all 0.12s ease;
  text-align: left;
  user-select: none;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  ${({ statusType, wktTpCd, isDimmed }) => {
    const style = getScheduleStyle(statusType, wktTpCd, isDimmed);
    return `
      background: ${style.bg};
      color: ${style.text};
      border: ${style.borderWidth} ${style.borderStyle || 'solid'} ${style.border};
      ${style.borderLeft ? `border-left: ${style.borderLeft};` : ''}
      text-decoration: ${style.lineThrough ? 'line-through' : 'none'};
      opacity: ${style.opacity};
      font-weight: ${statusType === 'today' || statusType === 'delayed' ? '600' : '500'};
      ${statusType === 'today' ? 'box-shadow: 0 1px 3px rgba(2, 132, 199, 0.15);' : ''}
      ${statusType === 'delayed' ? 'box-shadow: 0 1px 3px rgba(239, 68, 68, 0.18);' : ''}
    `;
  }}

  &[draggable='false'] {
    cursor: pointer;
  }

  &:active {
    cursor: ${({ statusType }) => (statusType === 'complete' ? 'pointer' : 'grabbing')};
    opacity: ${({ statusType }) => (statusType === 'complete' ? 1 : 0.6)};
  }

  &[draggable='false']:active {
    cursor: pointer;
    transform: none;
    box-shadow: none;
    opacity: 1;
  }

  &:hover {
    filter: brightness(0.96);
    transform: translateY(-1px);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  }
`;

export const CalMoreBtn = styled.button`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: none;
  border: none;
  padding: 1px 4px;
  cursor: pointer;
  text-align: left;
  align-self: flex-start;
  border-radius: 3px;
  flex-shrink: 0;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
`;

export const DragToast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: #0f172a;
  color: #ffffff;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// ==========================================
// Timeline & View Shared Components (WeekView / DayView / MonthView)
// ==========================================

export const CalEventTitleText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

export const TimelineGridContainer = styled.div<{
  minWidth?: number;
  gridTemplateColumns?: string;
}>`
  min-width: ${({ minWidth }) => (minWidth ? `${minWidth}px` : '100%')};
  display: grid;
  grid-template-columns: ${({ gridTemplateColumns }) =>
    gridTemplateColumns || '110px minmax(360px, 1fr)'};
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #ffffff;
`;

export const TimelineHeaderCol = styled.div`
  padding: 10px;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
  font-weight: 700;
  font-size: 12px;
  text-align: center;
  color: #334155;
`;

export const TimelineDayHeaderCell = styled.div<{ isToday?: boolean }>`
  padding: 10px;
  border-bottom: 1px solid #e2e8f0;
  border-left: 1px solid #e2e8f0;
  background-color: ${({ isToday }) => (isToday ? '#f0f9ff' : '#f8fafc')};
  text-align: center;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #e0f2fe;
  }
`;

export const TimelineDayNameText = styled.p<{ dayIndex?: number }>`
  margin: 0;
  color: ${({ dayIndex }) => (dayIndex === 0 ? '#dc2626' : dayIndex === 6 ? '#2563eb' : '#334155')};
  font-size: 12px;
  font-weight: 700;
`;

export const TimelineDayDateText = styled.p<{ isToday?: boolean }>`
  margin: 0;
  font-size: 12px;
  color: ${({ isToday }) => (isToday ? '#0284c7' : '#64748b')};
  font-weight: ${({ isToday }) => (isToday ? 700 : 500)};
`;

export const TimelineAllDayLabel = styled.div`
  padding: 8px;
  min-height: 52px;
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const TimelineAllDayContent = styled.div<{ isDragOver?: boolean }>`
  padding: 4px;
  min-height: 52px;
  border-top: 1px solid #e2e8f0;
  border-left: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background-color: ${({ isDragOver }) => (isDragOver ? '#f0f9ff' : 'transparent')};
  outline: ${({ isDragOver }) => (isDragOver ? '2px dashed #0284c7' : 'none')};
  outline-offset: -2px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ isDragOver }) => (isDragOver ? '#f0f9ff' : '#f8fafc')};
  }
`;

export const TimelineAllDayChip = styled.div<{
  bg?: string;
  borderColor?: string;
  borderWidth?: string;
  borderLeft?: string;
  textColor?: string;
  opacity?: number;
  lineThrough?: boolean;
}>`
  padding: 4px 6px;
  border-radius: 4px;
  background-color: ${({ bg }) => bg || '#f1f5f9'};
  border: ${({ borderWidth, borderColor }) =>
    `${borderWidth || '1px'} solid ${borderColor || '#cbd5e1'}`};
  ${({ borderLeft }) => (borderLeft ? `border-left: ${borderLeft};` : '')}
  color: ${({ textColor }) => textColor || '#334155'};
  opacity: ${({ opacity }) => (opacity !== undefined ? opacity : 1)};
  text-decoration: ${({ lineThrough }) => (lineThrough ? 'line-through' : 'none')};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.25;
  cursor: grab;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;

  &[draggable='false'] {
    cursor: pointer;
  }

  &:active {
    cursor: grabbing;
    opacity: 0.6;
  }

  &[draggable='false']:active {
    cursor: pointer;
    opacity: 1;
  }
`;

export const TimelineTimeAxisCol = styled.div`
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
  border-right: 1px solid #e2e8f0;
`;

export const TimelineTimeAxisGrid = styled.div<{
  height: number;
  rowCount: number;
}>`
  height: ${({ height }) => height}px;
  display: grid;
  grid-template-rows: repeat(${({ rowCount }) => rowCount}, 1fr);
`;

export const TimelineHourLabelCell = styled.div<{ isFirst?: boolean }>`
  padding: 0 8px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 4px;
  border-top: ${({ isFirst }) => (isFirst ? 'none' : '1px solid #e2e8f0')};
  font-size: 12px;
  font-weight: 700;
  color: #475569;
`;

export const TimelineDayColumnArea = styled.div<{
  height: number;
  hourHeight: number;
}>`
  position: relative;
  height: ${({ height }) => height}px;
  border-top: 1px solid #e2e8f0;
  border-left: 1px solid #e2e8f0;
  background-image: repeating-linear-gradient(
      to bottom,
      transparent 0,
      transparent ${({ hourHeight }) => hourHeight - 1}px,
      #e2e8f0 ${({ hourHeight }) => hourHeight - 1}px,
      #e2e8f0 ${({ hourHeight }) => hourHeight}px
    ),
    repeating-linear-gradient(
      to bottom,
      transparent 0,
      transparent ${({ hourHeight }) => hourHeight / 2 - 1}px,
      rgba(148, 163, 184, 0.25) ${({ hourHeight }) => hourHeight / 2 - 1}px,
      rgba(148, 163, 184, 0.25) ${({ hourHeight }) => hourHeight / 2}px
    );
  cursor: pointer;
`;

export const TimelineDragIndicatorBox = styled.div<{
  topPx: number;
  heightPx: number;
  leftPx?: number;
  rightPx?: number;
}>`
  position: absolute;
  top: ${({ topPx }) => topPx}px;
  height: ${({ heightPx }) => heightPx}px;
  left: ${({ leftPx }) => (leftPx !== undefined ? `${leftPx}px` : '4px')};
  right: ${({ rightPx }) => (rightPx !== undefined ? `${rightPx}px` : '4px')};
  border: 2px dashed #0284c7;
  border-radius: 6px;
  background-color: rgba(2, 132, 199, 0.12);
  color: #0369a1;
  font-size: 10.5px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 30;
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(2, 132, 199, 0.2);
  backdrop-filter: blur(1px);
`;

export const TimelineDragIndicatorBadge = styled.span`
  background: #ffffff;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #bae6fd;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  white-space: nowrap;
`;

export const TimelineTimedCard = styled.div<{
  topPx: number;
  heightPx: number;
  leftPct: number;
  widthPct: number;
  bg: string;
  borderColor: string;
  borderWidth: string;
  borderLeft?: string;
  textColor: string;
  opacity: number;
  fontSize?: number;
}>`
  position: absolute;
  top: ${({ topPx }) => topPx}px;
  height: ${({ heightPx }) => heightPx}px;
  left: calc(${({ leftPct }) => leftPct}% + 2px);
  width: calc(${({ widthPct }) => widthPct}% - 4px);
  border-radius: 6px;
  background-color: ${({ bg }) => bg};
  border: ${({ borderWidth, borderColor }) => `${borderWidth} solid ${borderColor}`};
  ${({ borderLeft }) => (borderLeft ? `border-left: ${borderLeft};` : '')}
  color: ${({ textColor }) => textColor};
  opacity: ${({ opacity }) => opacity};
  font-size: ${({ fontSize }) => (fontSize ? `${fontSize}px` : '11px')};
  font-weight: 600;
  line-height: 1.25;
  padding: 4px 6px;
  overflow: hidden;
  cursor: grab;
  z-index: 2;
  box-sizing: border-box;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.15s ease, filter 0.15s ease;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &[draggable='false'] {
    cursor: pointer;
  }

  &:active {
    cursor: grabbing;
    opacity: 0.5;
  }

  &[draggable='false']:active {
    cursor: pointer;
    opacity: 1;
  }

  &:hover {
    filter: brightness(0.97);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.12);
    z-index: 10;
  }
`;

export const TimelineTimedCardHeader = styled.div<{ lineThrough?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  text-decoration: ${({ lineThrough }) => (lineThrough ? 'line-through' : 'none')};
  display: flex;
  align-items: center;
`;

export const TimelineTimedCardTime = styled.span`
  font-size: 9.5px;
  opacity: 0.85;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.1;
  margin-top: 2px;
`;

export const TimelineMoreBadgeBtn = styled.button<{
  topPx: number;
  leftPct: number;
  widthPct: number;
}>`
  position: absolute;
  top: ${({ topPx }) => topPx}px;
  left: calc(${({ leftPct }) => leftPct}% + 2px);
  width: calc(${({ widthPct }) => widthPct}% - 4px);
  height: 26px;
  background-color: #f8fafc;
  border: 1px solid #cbd5e1;
  color: #0284c7;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 4;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.15s ease;

  &:hover {
    background-color: #e0f2fe;
    border-color: #0284c7;
    color: #0369a1;
  }
`;
